import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import MediaCoverage from '../components/MediaCoverage';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Play, TrendingUp, Users, Award, Shield, ArrowRight, Quote, Building2, Target } from 'lucide-react';

/**
 * /casos-de-exito — Landing B2B de testimonios reales de dueños de centros
 * de rehabilitación que crecieron con SinAdicciones.org. Foco: convertir
 * centros Gratis a planes pagados (Destacado / Premium / Elite).
 */

const TESTIMONIAL_VIDEOS = [
  {
    id: 'tdMG8Al_vAw',
    title: 'Testimonio de dueño de centro asociado',
    description: 'Un director de centro cuenta cómo SinAdicciones.org le trajo cotizaciones reales de familias que buscaban tratamiento en su región.',
  },
  {
    id: 'IJBvT2K0WSo',
    title: 'Recomendación de centro publicado en la plataforma',
    description: 'Otro centro comparte por qué recomienda la plataforma: leads calificados, visibilidad nacional y sin costos ocultos.',
  },
];

export default function CasosDeExitoPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="casos-de-exito-page">
      <title>Casos de Éxito · Centros que crecieron con SinAdicciones.org</title>
      <meta
        name="description"
        content="Centros de rehabilitación que multiplicaron sus cotizaciones y visibilidad publicando en SinAdicciones.org, la plataforma líder en Chile. Testimonios reales de directores y dueños de centros."
      />
      <link rel="canonical" href="https://sinadicciones.org/casos-de-exito" />
      <meta property="og:title" content="Casos de Éxito · Centros asociados a SinAdicciones.org" />
      <meta property="og:description" content="Testimonios reales de directores de centros de rehabilitación que crecieron publicando en la plataforma líder de Chile." />
      <meta property="og:url" content="https://sinadicciones.org/casos-de-exito" />
      <meta property="og:type" content="article" />

      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800/60">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 via-slate-950 to-slate-950 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-6" data-testid="hero-badge">
            <Award className="w-4 h-4" /> Casos de éxito · Centros asociados
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight mb-6" data-testid="hero-title">
            Centros que ya <span className="text-teal-400">multiplicaron sus cotizaciones</span> con SinAdicciones.org
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-3xl leading-relaxed mb-8" data-testid="hero-subtitle">
            Somos la <strong className="text-white">plataforma de rehabilitación más consultada de Chile</strong>. Estos son los testimonios de directores y dueños de centros que hoy reciben leads calificados, visibilidad nacional y familias listas para tratamiento — todo en piloto automático.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/planes" data-testid="hero-cta-planes">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg">
                Ver planes para centros <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/agregar-centro" data-testid="hero-cta-agregar">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 font-semibold px-6 py-3 rounded-lg">
                Suma tu centro
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* STATS — mezcla plataforma + valor al centro */}
      <section className="bg-slate-900/40 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div data-testid="stat-familias">
            <div className="text-3xl md:text-4xl font-bold text-teal-400">10.000+</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Familias asesoradas</div>
          </div>
          <div data-testid="stat-cotizaciones">
            <div className="text-3xl md:text-4xl font-bold text-teal-400">300+</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Cotizaciones al mes</div>
          </div>
          <div data-testid="stat-regiones">
            <div className="text-3xl md:text-4xl font-bold text-teal-400">16</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Regiones de Chile</div>
          </div>
          <div data-testid="stat-prensa">
            <div className="text-3xl md:text-4xl font-bold text-teal-400">15+</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Apariciones en prensa</div>
          </div>
        </div>
      </section>

      {/* VIDEOS TESTIMONIOS */}
      <section className="max-w-6xl mx-auto px-4 py-16 md:py-24" data-testid="videos-section">
        <div className="mb-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">
            <Play className="w-4 h-4" /> Testimonios en video
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Escúchalo de los propios centros
          </h2>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed">
            Directores y dueños de centros de rehabilitación cuentan qué cambió en su operación desde que publicaron en SinAdicciones.org.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {TESTIMONIAL_VIDEOS.map((v) => (
            <Card key={v.id} className="bg-slate-900 border-slate-800 overflow-hidden" data-testid={`video-card-${v.id}`}>
              <div className="relative w-full" style={{ paddingBottom: '177.78%' /* 9:16 vertical shorts */ }}>
                <iframe
                  src={`https://www.youtube.com/embed/${v.id}?rel=0&modestbranding=1&playsinline=1`}
                  title={v.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  className="absolute inset-0 w-full h-full"
                  data-testid={`video-iframe-${v.id}`}
                />
              </div>
              <CardContent className="p-6">
                <Quote className="w-6 h-6 text-teal-400 mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">{v.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{v.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* MEDIOS EN PRENSA */}
      <section className="border-t border-slate-800/60 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24" data-testid="prensa-section">
          <div className="mb-12 max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">
              <Shield className="w-4 h-4" /> Prensa nacional
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
              Los medios nos avalan
            </h2>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
              Publicar tu centro en SinAdicciones.org también es asociarte a una marca con cobertura permanente en prensa nacional, académicos e investigadores del área.
            </p>
          </div>

          <MediaCoverage />
        </div>
      </section>

      {/* VALUE PROPS — beneficios para el centro */}
      <section className="max-w-6xl mx-auto px-4 py-16 md:py-24" data-testid="value-props">
        <div className="mb-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">
            <Building2 className="w-4 h-4" /> Por qué elegirnos
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Lo que ganas al publicar tu centro
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="bg-slate-900 border-slate-800" data-testid="value-leads">
            <CardContent className="p-6">
              <Target className="w-8 h-8 text-teal-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Leads calificados y en tiempo real</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Los planes pagados reciben el contacto directo de familias que ya declararon interés, ubicación y presupuesto. Sin intermediarios.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800" data-testid="value-visibilidad">
            <CardContent className="p-6">
              <TrendingUp className="w-8 h-8 text-teal-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Visibilidad nacional 24/7</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Aparece en las búsquedas orgánicas de Google, en el directorio por región y en las comparativas que ven miles de familias cada mes.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800" data-testid="value-verificacion">
            <CardContent className="p-6">
              <Users className="w-8 h-8 text-teal-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Sello de centro verificado</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Nuestro equipo periodístico verifica cada centro. Ese sello te diferencia frente a la competencia informal y aumenta la conversión.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA FINAL — a /planes */}
      <section className="border-t border-slate-800/60 bg-gradient-to-br from-teal-900/30 via-slate-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-24 text-center" data-testid="cta-planes-section">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4">
            Para centros de rehabilitación
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            ¿Tienes un centro? Únete a la plataforma líder
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Recibe cotizaciones de familias que buscan tratamiento en tu región. Los planes pagados reciben los datos de contacto en tiempo real y aparecen destacados en el directorio.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/planes" data-testid="cta-final-planes">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-lg text-base">
                Ver planes para centros <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/agregar-centro" data-testid="cta-final-agregar">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 font-semibold px-8 py-4 rounded-lg text-base">
                Publicar mi centro
              </Button>
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-6">
            Planes desde $99.000/mes · Cancelación cuando quieras · Verificación gratuita
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
